import {JSX, useEffect, useRef, useState} from 'react';
import menu from '../shared/data/menu.json';
import DropdownMenu from './common/Dropdown';
import {
	ArrowsRightLeftIcon,
	PencilIcon,
	TrashIcon,
	UserIcon,
	UserCircleIcon,
	ArrowRightOnRectangleIcon,
	ArrowsPointingOutIcon,
	EyeIcon,
	EyeSlashIcon,
	QuestionMarkCircleIcon,
	ArrowsPointingInIcon,
	BookOpenIcon,
	TableCellsIcon,
	BanknotesIcon,
	Bars3Icon,
	XMarkIcon,
	ChevronRightIcon,
	HomeIcon
} from '@heroicons/react/24/solid';
import { ClipboardDocumentListIcon, Cog8ToothIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '../hooks/UseLogout';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/ApiService';
import { UserProfile } from '../shared/interfaces';
import { apiRoutes } from '../shared/apiRoutes';
import ShortcutInformationDialog from './common/ShortcutInformationDialog';
import SideDrawer from './common/SideDrawer';
import Button from './common/Button';
import { useAuthorization } from '../auth/useAuthorization';
import { Role } from '../auth/roles';
import {
	DATABASE_BACKUP,
	GAME_LIST,
	HAVLA_CREATE,
	IMPORT_ACCOUNT,
	MATCH_RECALL,
	PARTY_CREATE,
	PARTY_LIST,
	QUICK_ADD_PARTY,
	REPORT_HAVALA,
	REPORT_LEDGER,
	REPORT_MATCH,
	REPORT_TRIAL_BALANCE,
	USER_LIST,
} from '../auth/permissions';
import { formatDateOnly } from '../utils/util';
import TwoFactorSettings from './auth/TwoFactorSettings';

/** Utility submenu entries (see `menu.json`) visible only for `Role.ADMIN`, not Owner or other roles. */
const UTILITY_MENU_ADMIN_ONLY = new Set(['tenant', 'tenant activities', 'create notification']);

/** Hidden for Admin users (they only see Utility; these entries are omitted from Utility for Admin). */
const UTILITY_MENU_EXCLUDED_FOR_ADMIN = new Set(['quick add party', 'match recall']);

/** Menu label -> permission. Items not listed stay visible unless a special case below removes them. */
const MENU_ITEM_PERMISSION: Record<string, string> = {
	'one day': GAME_LIST,
	'test': GAME_LIST,
	'session': GAME_LIST,
	'party': PARTY_LIST,
	'ledger account': REPORT_LEDGER,
	'havala account': REPORT_HAVALA,
	'trial balance': REPORT_TRIAL_BALANCE,
	'havala report': REPORT_HAVALA,
	'select match': REPORT_MATCH,
	'users': USER_LIST,
	'quick add party': QUICK_ADD_PARTY,
	'match recall': MATCH_RECALL,
	'import account': IMPORT_ACCOUNT,
	'database backup': DATABASE_BACKUP,
};

interface menuItem {
	title: string;
	icon: string;
	items: string[];
}

const iconMap: Record<string, JSX.Element> = {
	ArrowsRightLeftIcon: <ArrowsRightLeftIcon className="ml-2 mr-1 h-5 w-5" />,
	Cog8ToothIcon: <Cog8ToothIcon className="ml-2 mr-1 h-5 w-5" />,
	ClipboardDocumentListIcon: <ClipboardDocumentListIcon className="ml-2 mr-1 h-5 w-5" />,
	UserIcon: <UserIcon className="ml-2 mr-1 h-5 w-5" />
};

const mobileMenuIconMap: Record<string, JSX.Element> = {
	ArrowsRightLeftIcon: <ArrowsRightLeftIcon className="h-5 w-5 text-blue-400" />,
	Cog8ToothIcon: <Cog8ToothIcon className="h-5 w-5 text-blue-400" />,
	ClipboardDocumentListIcon: <ClipboardDocumentListIcon className="h-5 w-5 text-blue-400" />,
	UserIcon: <UserIcon className="h-5 w-5 text-blue-400" />
};

const Header: React.FC = () => {
	const [profile, setProfile] = useState<UserProfile>({
		userName: '',
		fullName: '',
		firstName: '',
		modifiedAt: new Date()});
	const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
	const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
	const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false);
	const [isChangePasswordOpen, setIsChangePasswordOpen] = useState<boolean>(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
	const [expandedMobileSection, setExpandedMobileSection] = useState<string | null>(null);
	const mobileMenuRef = useRef<HTMLDivElement>(null);
	const [changePasswordData, setChangePasswordData] = useState({
		currentPassword: '',
		newPassword: '',
		confirmPassword: '',
	});
	const [changePasswordErrors, setChangePasswordErrors] = useState<Record<string, string>>({});
	const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
	const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
	const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
	const newPasswordRef = useRef<HTMLInputElement>(null);
	const confirmPasswordRef = useRef<HTMLInputElement>(null);
	const [teamCutToolbarVisible, setTeamCutToolbarVisible] = useState(false);
	const [isBackingUp, setIsBackingUp] = useState(false);
	const navigate = useNavigate();
	const logout = useLogout();
	const { theme, setTheme } = useAuth();
	const { userRoles, hasRole, hasPermission } = useAuthorization();
	const isPartner = hasRole('Partner');
	const isAdmin = hasRole(Role.ADMIN);
	const canOpenLedger = hasPermission(REPORT_LEDGER);
	const canOpenTrial = hasPermission(REPORT_TRIAL_BALANCE);
	const canOpenHavalaPages = hasPermission(REPORT_HAVALA);
	const canOpenSelectMatch = hasPermission(REPORT_MATCH);
	const canCreateHavala = hasPermission(HAVLA_CREATE);
	const canCreateParty = hasPermission(PARTY_CREATE);
	const canQuickAddParty = hasPermission(QUICK_ADD_PARTY);
	const canOpenGames = hasPermission(GAME_LIST);

	const handleDatabaseBackup = async () => {
		if (isBackingUp) return;
		setIsBackingUp(true);
		try {
			const response = await apiService.getBlob(apiRoutes.utility.tenantDatabaseBackup);
			const disposition = response.headers['content-disposition'] || '';
			const filenameMatch = disposition.match(/filename[^;=\n]*=\s*"?([^";\n]+)"?/i);
			const filename = filenameMatch?.[1] || `backup-${new Date().toISOString().replace(/[:.]/g, '').slice(0, 15)}_utc.bkp`;

			const url = URL.createObjectURL(response.data);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);

			window.dispatchEvent(new CustomEvent('show-toast', {
				detail: { message: 'Database backup downloaded successfully', type: 'success' }
			}));
		} catch (err: any) {
			const msg = err?.error || err?.message || 'Failed to download backup';
			window.dispatchEvent(new CustomEvent('show-toast', {
				detail: { message: msg, type: 'error' }
			}));
		} finally {
			setIsBackingUp(false);
		}
	};

	const handleAction = (action: string) => {
		switch (action.toLowerCase()) {
			case 'one day':
				navigate('/game/oneday');
				break;
			case 'test':
				navigate('/game/test');
				break;
			case 'session':
				navigate('/game/session');
				break;
			case 'party':
				navigate('/party');
				break;
			case 'ledger account':
				navigate('/report/ledger');
				break;
			case 'havala account':
				navigate('/report/havala');
				break;
			case 'trial balance':
				navigate('/report/trial');
				break;
			case 'havala report':
				navigate('/report/havala-report');
				break;
			case 'select match':
				navigate('/report/select-match');
				break;
			case 'users':
				navigate('/utility/users');
				break;
			case 'tenant':
				navigate('/utility/tenant');
				break;
			case 'tenant activities':
				navigate('/utility/tenant-activities');
				break;
			case 'quick add party':
				navigate('/utility/add-party');
				break;
			case 'match recall':
				navigate('/utility/match-recall');
				break;
			case 'interest calculation':
				navigate('/utility/interest-calculation');
				break;
		case 'create notification':
			navigate('/utility/create-notification');
			break;
		case 'import account':
			navigate('/utility/import-account');
			break;
		case 'soda form sequence':
			navigate('/utility/soda-form-sequence');
			break;
		case 'database backup':
			handleDatabaseBackup();
			break;
			case 'profile':
				setIsProfileOpen(true);
				break;
			case 'change password':
				setIsChangePasswordOpen(true);
				break;
			case 'logout':
				logout();
				break;
			case 'home':
				navigate('/home');
				break;
			default:
				break;
		}
	}

	const handleMobileAction = (action: string) => {
		setIsMobileMenuOpen(false);
		setExpandedMobileSection(null);
		handleAction(action);
	};

	const toggleMobileSection = (title: string) => {
		setExpandedMobileSection(prev => prev === title ? null : title);
	};

	// Close mobile menu on outside click
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
				setIsMobileMenuOpen(false);
				setExpandedMobileSection(null);
			}
		};
		if (isMobileMenuOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isMobileMenuOpen]);

	// Close mobile menu on route change
	useEffect(() => {
		setIsMobileMenuOpen(false);
		setExpandedMobileSection(null);
	}, []);

	const handleMaximize = async () => {
		try {
			if (!document.fullscreenElement) {
				const elem: any = document.documentElement;
				await elem.requestFullscreen();
			} else {
				await document.exitFullscreen();
			}
		} catch (err) {
			console.error('Fullscreen toggle failed:', err);
		}
	};

	const handleHelp = () => {
		setIsHelpOpen(true);
	};

	useEffect(() => {
		const onToolbar = (e: Event) => {
			const d = (e as CustomEvent<{ visible?: boolean }>).detail;
			setTeamCutToolbarVisible(Boolean(d?.visible));
		};
		window.addEventListener('game-cut-toolbar', onToolbar as EventListener);
		return () => window.removeEventListener('game-cut-toolbar', onToolbar as EventListener);
	}, []);

	useEffect(() => {
		const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
		document.addEventListener('fullscreenchange', onFsChange);
		// Vendor-prefixed events for broader support
		document.addEventListener('webkitfullscreenchange', onFsChange as any);
		document.addEventListener('mozfullscreenchange', onFsChange as any);
		document.addEventListener('MSFullscreenChange', onFsChange as any);
		return () => {
			document.removeEventListener('fullscreenchange', onFsChange);
			document.removeEventListener('webkitfullscreenchange', onFsChange as any);
			document.removeEventListener('mozfullscreenchange', onFsChange as any);
			document.removeEventListener('MSFullscreenChange', onFsChange as any);
		};
	}, []);

	useEffect(() => {
		apiService.get(apiRoutes.auth.profile)
			.then(response => { // Debug log
				
				// Handle different response structures
				const profileData = (response as any).data || response;
				
				// Handle different field name variations
				const userName = profileData.userName || profileData.username || profileData.user_name || '';
				const fullName = profileData.fullName || profileData.full_name || profileData.name || userName;
				const firstName = profileData.firstName || profileData.first_name || '';
				const modifiedAt = profileData.modifiedAt || profileData.modified_at || profileData.updatedAt || new Date(); // Debug log
				
				setProfile({
					userName: userName,
					fullName: fullName,
					firstName: firstName,
					modifiedAt: new Date(modifiedAt)
				});
			})
			.catch(error => {
				console.error('Error fetching user profile:', error);
			});
	}, []);

	const getNewPasswordError = (): string => {
		const p = changePasswordData.newPassword;
		if (!p.trim()) return 'New password is required';
		if (p.length < 8) return 'New password must be at least 8 characters';
		if (p.length > 20) return 'New password must be at most 20 characters';
		if (!/[A-Z]/.test(p)) return 'Must contain at least 1 uppercase letter';
		if (!/[a-z]/.test(p)) return 'Must contain at least 1 lowercase letter';
		if (!/\d/.test(p)) return 'Must contain at least 1 digit';
		if (!/[^A-Za-z0-9]/.test(p)) return 'Must contain at least 1 special character';
		return '';
	};

	const validateChangePassword = () => {
		const errors: Record<string, string> = {};
		
		if (!changePasswordData.currentPassword.trim()) {
			errors.currentPassword = 'Current password is required';
		}
		
		const newPwdError = getNewPasswordError();
		if (newPwdError) errors.newPassword = newPwdError;
		
		if (!changePasswordData.confirmPassword.trim()) {
			errors.confirmPassword = 'Please confirm your new password';
		} else if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
			errors.confirmPassword = 'Passwords do not match';
		}
		
		if (changePasswordData.currentPassword === changePasswordData.newPassword) {
			errors.newPassword = 'New password must be different from current password';
		}
		
		setChangePasswordErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleChangePasswordInput = (field: string, value: string) => {
		setChangePasswordData(prev => ({
			...prev,
			[field]: value
		}));
		// Clear error for this field when user starts typing
		if (changePasswordErrors[field]) {
			setChangePasswordErrors(prev => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const handleChangePasswordSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!validateChangePassword()) {
			return;
		}
		
		setIsChangingPassword(true);
		try {
			await apiService.post(apiRoutes.auth.changePassword, {
				currentPassword: changePasswordData.currentPassword,
				newPassword: changePasswordData.newPassword,
				confirmPassword: changePasswordData.confirmPassword,
			});
			
			window.dispatchEvent(new CustomEvent('show-toast', {
				detail: { message: 'Password changed successfully', type: 'success' }
			}));
			
			// Reset form and close drawer
			setChangePasswordData({
				currentPassword: '',
				newPassword: '',
				confirmPassword: '',
			});
			setChangePasswordErrors({});
			setIsChangePasswordOpen(false);
		} catch (error: any) {
			console.error('Error changing password:', error);
			let errorMessage = 'Failed to change password. Please try again.';
			if (error?.error) errorMessage = error.error;
			else if (error?.message) errorMessage = error.message;
			else if (Array.isArray(error?.details) && error.details.length > 0) {
				errorMessage = error.details.map((d: { errorMessage?: string }) => d.errorMessage).filter(Boolean).join(' ');
			} else if (typeof error?.details === 'string') errorMessage = error.details;
			window.dispatchEvent(new CustomEvent('show-toast', {
				detail: { message: errorMessage, type: 'error' }
			}));
		} finally {
			setIsChangingPassword(false);
		}
	};

	const handleCloseChangePassword = () => {
		setChangePasswordData({
			currentPassword: '',
			newPassword: '',
			confirmPassword: '',
		});
		setChangePasswordErrors({});
		setIsChangePasswordOpen(false);
	};

	// Enter key: move to next field or submit on last field
	const handleCurrentPasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			newPasswordRef.current?.focus();
		}
	};
	const handleNewPasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			confirmPasswordRef.current?.focus();
		}
	};
	const handleConfirmPasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (validateChangePassword()) {
				handleChangePasswordSubmit({ preventDefault: () => {} } as React.FormEvent);
			}
		}
	};

  // Global key handler for Help (F1) and close (Esc) and app shortcuts
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (!e || typeof e.key !== 'string') return;     // guard
  const key = (e.key || '').toLowerCase();         // safe
  const code = (e.code || '').toString();

  // RBAC: Partners can only use ALT + F7 shortcut, all others are disabled
  // BUT allow F3 for party list reference (useful for all users)
  // Admin: no game/party/report shortcuts (Utility-only UI); allow F11 fullscreen
  if (isAdmin && !isPartner) {
    const adminActive = document.activeElement;
    const adminInputFocused = adminActive?.tagName === 'INPUT' ||
      adminActive?.tagName === 'TEXTAREA' ||
      adminActive?.getAttribute('contenteditable') === 'true';
    if (!adminInputFocused && e.key === 'F11') {
      setIsFullscreen(prev => !prev);
    }
    return;
  }

  if (isPartner) {
    // Allow F3 for party list reference
    if (e.key === 'F3' && !e.altKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('open-party-list-reference'));
      return;
    }
    // Allow only ALT + F7 for Partners
    if (e.altKey && !e.ctrlKey && e.key === 'F7') {
      e.preventDefault(); 
      window.dispatchEvent(new CustomEvent('open-party-soda')); 
      return; 
    }
    // Block all other shortcuts for Partners
    return;
  }

  // Don't trigger shortcuts when typing in input fields
  const activeElement = document.activeElement;
  const isInputFocused = activeElement?.tagName === 'INPUT' || 
                         activeElement?.tagName === 'TEXTAREA' ||
                         activeElement?.getAttribute('contenteditable') === 'true';

  const cutPanelEl = document.getElementById('team-cuts-inline-panel');
  const inTeamCutPanel =
    Boolean(
      cutPanelEl &&
      activeElement &&
      cutPanelEl.contains(activeElement),
    );

  if (
    (!isInputFocused || inTeamCutPanel) &&
    (e.key === 'F9' || e.code === 'F9') &&
    !e.altKey &&
    !e.ctrlKey &&
    !e.shiftKey &&
    teamCutToolbarVisible
  ) {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('open-team-cut'));
    return;
  }

  // Help dialog
  if (e.key === 'F1') { e.preventDefault(); setIsHelpOpen(true); return; }
  if (e.key === 'F3') { e.preventDefault(); window.dispatchEvent(new CustomEvent('open-party-list-reference')); return; }
  if (e.key === 'F4' && canQuickAddParty) { e.preventDefault(); window.dispatchEvent(new CustomEvent('open-quick-add-party')); return; }
  if (e.key === 'F11') { setIsFullscreen(prev => !prev); }

  // Ctrl + Alt shortcuts (use code for letters)
  if (e.ctrlKey && e.altKey && !e.shiftKey) {
    if (code === 'KeyO' && canOpenGames) { e.preventDefault(); navigate('/game/oneday'); setTimeout(() => window.dispatchEvent(new Event('open-create-game')), 150); return; }
    if (code === 'KeyT' && canOpenGames) { e.preventDefault(); navigate('/game/test'); setTimeout(() => window.dispatchEvent(new Event('open-create-game')), 150); return; }
    if (code === 'KeyS' && canOpenGames) { e.preventDefault(); navigate('/game/session'); setTimeout(() => window.dispatchEvent(new Event('open-create-game')), 150); return; }
    if (code === 'KeyP' && canCreateParty) { e.preventDefault(); navigate('/party'); setTimeout(() => window.dispatchEvent(new Event('open-create-party')), 150); return; }
  }

  // Ctrl + Alt + Shift
  if (e.ctrlKey && e.altKey && e.shiftKey) {
    if (code === 'KeyH' && canOpenHavalaPages) { e.preventDefault(); navigate('/report/havala-report'); return; }
    if (code === 'KeyR' && canOpenSelectMatch) { e.preventDefault(); navigate('/report/select-match'); return; }
  }

  // Alt-only
  if (e.altKey && !e.ctrlKey && !isInputFocused) {
    if (!e.shiftKey && code === 'KeyL' && canOpenLedger) { e.preventDefault(); navigate('/report/ledger'); return; }
    if (e.shiftKey && code === 'KeyH' && canOpenHavalaPages) { e.preventDefault(); navigate('/report/havala'); return; }
    if (!e.shiftKey && code === 'KeyM' && canCreateHavala) {
      e.preventDefault();
      if (window.location.pathname === '/report/havala') {
        window.dispatchEvent(new CustomEvent('open-create-havala-side'));
      } else {
        navigate('/report/havala');
      }
      return;
    }
    if (!e.shiftKey && code === 'KeyH' && canCreateHavala) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('open-create-havala'));
      return;
    }
    if (!e.shiftKey && code === 'KeyT' && canOpenTrial) { e.preventDefault(); navigate('/report/trial'); return; }
    if (!e.shiftKey && code === 'KeyQ' && canQuickAddParty) { e.preventDefault(); navigate('/utility/add-party'); setTimeout(() => window.dispatchEvent(new Event('quick-add-party-save')), 150); return; }
    if (e.key === 'F12') { e.preventDefault(); window.dispatchEvent(new Event('match-over')); return; }
    if (e.key === 'F7') { e.preventDefault(); window.dispatchEvent(new CustomEvent('open-party-soda')); return; }
  }
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [navigate, isPartner, isAdmin, teamCutToolbarVisible, canOpenLedger, canOpenTrial, canOpenHavalaPages, canOpenSelectMatch, canCreateHavala, canCreateParty, canQuickAddParty, canOpenGames]);
		

	const filteredMenu = menu
		.filter((menuItem: menuItem) => {
			if (isAdmin) {
				return menuItem.title === 'Utility';
			}
			if (isPartner) {
				return menuItem.title === 'Soda' || menuItem.title === 'Utility';
			}
			return true;
		})
		.map((m: menuItem) => {
			let filteredItems = m.items;
			const allowItem = (item: string) => {
				const lower = item.toLowerCase();
				if (m.title === 'Utility') {
					if (isPartner) return lower === 'change password';
					if (isAdmin && UTILITY_MENU_EXCLUDED_FOR_ADMIN.has(lower)) return false;
					if (UTILITY_MENU_ADMIN_ONLY.has(lower)) return isAdmin;
					if (lower === 'soda form sequence') return hasRole(Role.OWNER);
					if (lower === 'change password') return true;
				}
				const required = MENU_ITEM_PERMISSION[lower];
				return required ? hasPermission(required) : true;
			};
			if (m.title === 'Utility' || m.title === 'Reports' || m.title === 'Soda') {
				filteredItems = m.items.filter(allowItem);
			}
			if (m.title === 'Party' && !hasPermission(PARTY_LIST)) {
				return null;
			}
			if ((m.title === 'Utility' || m.title === 'Reports' || m.title === 'Soda') && filteredItems.length === 0) {
				return null;
			}
			return { ...m, items: filteredItems };
		})
		.filter((m): m is menuItem => m !== null);

	return (
		<>
		<header className="sticky top-0 z-50 border-0 bg-blue-800 text-white py-0.5 px-1 sm:px-2 shadow-none">
			<nav className="flex justify-between items-center min-h-[36px]">
				{/* ===== DESKTOP NAV (hidden on mobile) ===== */}
				<ul className="hidden md:flex space-x-2 lg:space-x-4 items-center">
					<li>
						<button
							onClick={() => { navigate('/home'); setIsMobileMenuOpen(false); }}
							className="p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex items-center justify-center"
							title="Home"
						>
							<HomeIcon className="h-5 w-5" />
						</button>
					</li>
					{filteredMenu.map((m: menuItem, index: number) => (
						<li key={m.title + index}>
							<a href="#home" className="hover:underline">
							<DropdownMenu title={m.title} items={m.items} icon={iconMap[m.icon]} onSelectFunction={handleAction} />
							</a>
						</li>
					))}
				</ul>

				{/* ===== MOBILE NAV — home + hamburger + app name (hidden on desktop) ===== */}
				<div className="flex md:hidden items-center gap-1.5 sm:gap-2 min-w-0">
					<button
						onClick={() => { navigate('/home'); setIsMobileMenuOpen(false); }}
						className="p-1 sm:p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex-shrink-0"
						title="Home"
						aria-label="Home"
					>
						<HomeIcon className="h-5 w-5 sm:h-6 sm:w-6" />
					</button>
					<button
						onClick={() => setIsMobileMenuOpen(prev => !prev)}
						className="p-1 sm:p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex-shrink-0"
						aria-label="Toggle menu"
					>
						{isMobileMenuOpen
							? <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
							: <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />}
					</button>
					<span className="text-xs sm:text-sm font-semibold tracking-wide truncate">MyHisab</span>
				</div>

				{/* ===== RIGHT-SIDE ICONS (visible on both, compact on mobile) ===== */}
				<div className="flex gap-0.5 sm:gap-1 md:gap-3 lg:gap-4 items-center flex-shrink-0 min-w-0">
					{!isAdmin && canOpenHavalaPages ? (
						<button 
							onClick={() => { navigate('/report/havala'); setIsMobileMenuOpen(false); }}
							className="p-1 sm:p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex items-center justify-center flex-shrink-0"
							title="Havala Account (Alt + Shift + H)"
						>
							<BanknotesIcon className="h-4 w-4 sm:h-5 sm:w-5" />
						</button>
					) : null}
					{!isAdmin && canOpenLedger ? (
						<button 
							onClick={() => { navigate('/report/ledger'); setIsMobileMenuOpen(false); }}
							className="p-1 sm:p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex items-center justify-center flex-shrink-0"
							title="Ledger Account (Alt + L)"
						>
							<BookOpenIcon className="h-4 w-4 sm:h-5 sm:w-5" />
						</button>
					) : null}
					{!isAdmin && canOpenTrial ? (
						<button 
							onClick={() => { navigate('/report/trial'); setIsMobileMenuOpen(false); }}
							className="p-1 sm:p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex items-center justify-center flex-shrink-0"
							title="Trial Balance (Alt + T)"
						>
							<TableCellsIcon className="h-4 w-4 sm:h-5 sm:w-5" />
						</button>
					) : null}
					<button 
						onClick={handleMaximize}
						className="hidden md:flex p-1 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors items-center justify-center flex-shrink-0"
						title={isFullscreen ? 'Exit Fullscreen (F11)' : 'Enter Fullscreen (F11)'}
					>
						{isFullscreen ? (
							<ArrowsPointingInIcon className="h-5 w-5" />
						) : (
							<ArrowsPointingOutIcon className="h-5 w-5" />
						)}
					</button>
					{!isAdmin ? (
						<button 
							onClick={handleHelp}
							className="hidden md:flex p-1 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors items-center justify-center flex-shrink-0"
							title="Help (F1)"
						>
							<QuestionMarkCircleIcon className="h-5 w-5" />
						</button>
					) : null}
					<div className="flex overflow-hidden rounded border border-black/20 text-[10px] sm:text-xs leading-none" role="group" aria-label="Appearance">
						{([
							['default', 'Default'],
							['dark', 'Dark'],
							['light', 'Light'],
						] as const).map(([mode, label]) => (
							<button
								key={mode}
								type="button"
								onClick={() => { void setTheme(mode); }}
								className={`px-1.5 py-1 sm:px-2 ${theme === mode ? 'bg-black/20 font-semibold' : 'hover:bg-black/10'}`}
								aria-pressed={theme === mode}
							>
								{label}
							</button>
						))}
					</div>
					<button
						onClick={() => { setIsProfileOpen(true); setIsMobileMenuOpen(false); }}
						className="p-1 sm:p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex items-center justify-center flex-shrink-0"
						title="Profile"
					>
						<UserCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
					</button>
					<button
						type="button"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setIsMobileMenuOpen(false);
							logout();
						}}
						className="p-2 sm:p-1.5 rounded hover:bg-blue-700 active:bg-blue-600 transition-colors flex items-center justify-center flex-shrink-0 min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 md:p-1"
						style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
						title="Logout"
						aria-label="Logout"
					>
						<ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
					</button>
				</div>
			</nav>

			{/* ===== MOBILE SLIDE-DOWN MENU ===== */}
			<div
				ref={mobileMenuRef}
				className={`md:hidden absolute left-0 right-0 top-full bg-blue-900 shadow-xl z-50 overflow-hidden transition-all duration-300 ease-in-out ${
					isMobileMenuOpen ? 'max-h-[80vh] opacity-100 overflow-y-auto border-t border-blue-700' : 'max-h-0 opacity-0 pointer-events-none border-0'
				}`}
			>
				{/* Home - First item in mobile menu */}
				<button
					onClick={() => handleMobileAction('Home')}
					className="w-full flex items-center space-x-3 px-4 py-3 text-left text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-600 transition-colors border-b border-blue-800/50"
				>
					<HomeIcon className="h-5 w-5 text-blue-400" />
					<span>Home</span>
				</button>
				{/* Menu Sections */}
				{filteredMenu.map((m: menuItem, idx: number) => {
					const displayTitle = m.title;
					const hasItems = m.items.length > 0;
					const isExpanded = expandedMobileSection === m.title;

					if (!hasItems) {
						return (
							<button
								key={m.title + idx}
								onClick={() => handleMobileAction(m.title)}
								className="w-full flex items-center space-x-3 px-4 py-3 text-left text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-600 transition-colors border-b border-blue-800/50"
							>
								{mobileMenuIconMap[m.icon]}
								<span>{displayTitle}</span>
							</button>
						);
					}

					return (
						<div key={m.title + idx} className="border-b border-blue-800/50">
							<button
								onClick={() => toggleMobileSection(m.title)}
								className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-600 transition-colors"
							>
								<div className="flex items-center space-x-3">
									{mobileMenuIconMap[m.icon]}
									<span>{displayTitle}</span>
								</div>
								<ChevronRightIcon className={`h-4 w-4 text-blue-300 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
							</button>
							<div className={`bg-blue-950/50 overflow-hidden transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
								{m.items.map((item, itemIdx) => (
									<button
										key={item + itemIdx}
										onClick={() => handleMobileAction(item)}
										className="w-full text-left text-sm text-blue-100 px-10 py-2.5 hover:bg-blue-700 active:bg-blue-600 transition-colors"
									>
										{item}
									</button>
								))}
							</div>
						</div>
					);
				})}
			</div>
		</header>
		<div className="h-0.5" />

		<ShortcutInformationDialog
			isOpen={isHelpOpen}
			onClose={() => setIsHelpOpen(false)}
			isPartner={isPartner}
			isAdmin={isAdmin}
		/>

		{/* Profile Side Drawer */}
		<SideDrawer 
			isOpen={isProfileOpen} 
			onClose={() => setIsProfileOpen(false)} 
			title="User Profile"
			width="w-96"
		>
			<div className="space-y-6">
				{/* Profile Header */}
				<div className="flex items-center space-x-4 pb-4 border-b border-gray-200">
					<div className="flex-shrink-0">
						<div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-semibold">
							{profile.firstName?.charAt(0).toUpperCase() || profile.fullName?.charAt(0).toUpperCase() || profile.userName?.charAt(0).toUpperCase() || 'U'}
						</div>
					</div>
					<div className="flex-1 min-w-0">
						<h3 className="text-lg font-semibold text-gray-900 truncate">
							{profile.fullName || profile.firstName || profile.userName || 'User'}
						</h3>
						<p className="text-sm text-gray-500 truncate">
							{profile.userName || 'Username not available'}
						</p>
						{userRoles && userRoles.length > 0 && (
							<p className="text-xs text-blue-600 font-medium mt-1">
								{userRoles.join(', ')}
							</p>
						)}
					</div>
				</div>
				<div className="flex items-center justify-between gap-3 pb-4 border-b border-gray-200">
					<span className="text-sm font-medium text-gray-700">Appearance</span>
					<div className="flex rounded-lg border border-gray-300 overflow-hidden">
						{([
							['default', 'Default'],
							['dark', 'Dark'],
							['light', 'Light'],
						] as const).map(([mode, label]) => (
							<button
								key={mode}
								type="button"
								onClick={() => { void setTheme(mode); }}
								className={`px-3 py-1.5 text-sm ${theme === mode ? 'bg-blue-600 text-white font-semibold' : 'bg-white text-gray-700'}`}
							>
								{label}
							</button>
						))}
					</div>
				</div>

				{/* Profile Details */}
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							First Name
						</label>
						<div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
							{profile.firstName || 'Not available'}
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Full Name
						</label>
						<div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
							{profile.fullName || 'Not available'}
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							User Role
						</label>
						<div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
							{userRoles && userRoles.length > 0 ? userRoles.join(', ') : 'Not available'}
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Username
						</label>
						<div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
							{profile.userName || 'Not available'}
						</div>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Last Modified
						</label>
						<div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
							{profile.modifiedAt ? (formatDateOnly(profile.modifiedAt) || 'Not available') : 'Not available'}
						</div>
					</div>
				</div>

				<TwoFactorSettings isOpen={isProfileOpen} />
			</div>
		</SideDrawer>

		{/* Change Password Side Drawer */}
		<SideDrawer
			isOpen={isChangePasswordOpen}
			onClose={handleCloseChangePassword}
			title="Change Password"
			width="w-96"
		>
			<form onSubmit={handleChangePasswordSubmit} className="w-full">
				<div className="space-y-4">
					<div className="flex flex-col">
						<label htmlFor="currentPassword" className="block font-medium text-sm text-gray-700 mb-1">
							Current Password<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								type={showCurrentPassword ? "text" : "password"}
								id="currentPassword"
								value={changePasswordData.currentPassword}
								onChange={(e) => handleChangePasswordInput('currentPassword', e.target.value)}
								onKeyDown={handleCurrentPasswordKeyDown}
								className={`w-full p-2 pr-9 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
									changePasswordErrors.currentPassword ? 'border-2 border-red-600' : 'border-gray-300'
								}`}
								placeholder="Enter current password"
								disabled={isChangingPassword}
							/>
							<button
								type="button"
								tabIndex={-1}
								onClick={() => setShowCurrentPassword(!showCurrentPassword)}
								className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
								disabled={isChangingPassword}
							>
								{showCurrentPassword ? (
									<EyeSlashIcon className="h-4 w-4" />
								) : (
									<EyeIcon className="h-4 w-4" />
								)}
							</button>
						</div>
						{changePasswordErrors.currentPassword && (
							<p className="text-red-500 text-xs mt-1">{changePasswordErrors.currentPassword}</p>
						)}
					</div>

					<div className="flex flex-col">
						<label htmlFor="newPassword" className="block font-medium text-sm text-gray-700 mb-1">
							New Password<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								ref={newPasswordRef}
								type={showNewPassword ? "text" : "password"}
								id="newPassword"
								value={changePasswordData.newPassword}
								onChange={(e) => handleChangePasswordInput('newPassword', e.target.value)}
								onKeyDown={handleNewPasswordKeyDown}
								className={`w-full p-2 pr-9 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
									changePasswordErrors.newPassword ? 'border-2 border-red-600' : 'border-gray-300'
								}`}
								placeholder="Enter new password"
								maxLength={20}
								disabled={isChangingPassword}
							/>
							<button
								type="button"
								tabIndex={-1}
								onClick={() => setShowNewPassword(!showNewPassword)}
								className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
								disabled={isChangingPassword}
							>
								{showNewPassword ? (
									<EyeSlashIcon className="h-4 w-4" />
								) : (
									<EyeIcon className="h-4 w-4" />
								)}
							</button>
						</div>
						{changePasswordErrors.newPassword && (
							<p className="text-red-500 text-xs mt-1">{changePasswordErrors.newPassword}</p>
						)}
						<p className="text-xs text-gray-500 mt-1">8–20 chars: 1 uppercase, 1 lowercase, 1 digit, 1 special char</p>
					</div>

					<div className="flex flex-col">
						<label htmlFor="confirmPassword" className="block font-medium text-sm text-gray-700 mb-1">
							Confirm New Password<span className="text-red-500">*</span>
						</label>
						<div className="relative">
							<input
								ref={confirmPasswordRef}
								type={showConfirmPassword ? "text" : "password"}
								id="confirmPassword"
								value={changePasswordData.confirmPassword}
								onChange={(e) => handleChangePasswordInput('confirmPassword', e.target.value)}
								onKeyDown={handleConfirmPasswordKeyDown}
								className={`w-full p-2 pr-9 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
									changePasswordErrors.confirmPassword ? 'border-2 border-red-600' : 'border-gray-300'
								}`}
								placeholder="Confirm new password"
								maxLength={20}
								disabled={isChangingPassword}
							/>
							<button
								type="button"
								tabIndex={-1}
								onClick={() => setShowConfirmPassword(!showConfirmPassword)}
								className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
								disabled={isChangingPassword}
							>
								{showConfirmPassword ? (
									<EyeSlashIcon className="h-4 w-4" />
								) : (
									<EyeIcon className="h-4 w-4" />
								)}
							</button>
						</div>
						{changePasswordErrors.confirmPassword && (
							<p className="text-red-500 text-xs mt-1">{changePasswordErrors.confirmPassword}</p>
						)}
					</div>
				</div>

				<div className="flex justify-end space-x-2 pt-6 mt-6 border-t border-gray-200">
					<Button
						type="button"
						onClick={handleCloseChangePassword}
						disabled={isChangingPassword}
						className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Cancel
					</Button>
					<Button
						type="submit"
						isProcessing={isChangingPassword}
						className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
					>
						Change Password
					</Button>
				</div>
			</form>
		</SideDrawer>

		</>
	);
}

export default Header;